import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Users, QrCode, TrendingUp, Gift, Package, BarChart3, Loader2 } from "lucide-react";

const TIER_METAL_STYLE = {
  Silver:   { background: "linear-gradient(145deg, #F4F7FA 0%, #B6C2D0 55%, #6F7A8A 100%)", color: "#1A2530", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(0,0,0,0.12)" },
  Gold:     { background: "linear-gradient(145deg, #FBE38B 0%, #D4A93A 55%, #8C6618 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 6px rgba(0,0,0,0.15)" },
  Platinum: { background: "linear-gradient(145deg, #2B50B0 0%, #173075 55%, #0A1736 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 6px rgba(0,0,0,0.2)" },
};
const tierMetalStyle = (t) => TIER_METAL_STYLE[t] || TIER_METAL_STYLE.Silver;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await API.get("/admin/dashboard");
        setData(d);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#008A99]" /></div>;
  if (!data) return <div className="text-center py-20 text-gray-400">Failed to load dashboard</div>;

  const stats = [
    { label: "Total Users", value: data.total_users, icon: Users, color: "text-[#008A99]", bg: "bg-[#E0F4F5]" },
    { label: "Total Scans", value: data.total_scans, icon: QrCode, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Points Issued", value: data.total_points_issued?.toLocaleString(), icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Points Redeemed", value: data.total_points_redeemed?.toLocaleString(), icon: Gift, color: "text-red-500", bg: "bg-red-50" },
    { label: "Products", value: data.total_products, icon: Package, color: "text-[#008A99]", bg: "bg-[#E0F4F5]" },
    { label: "QR Codes", value: data.total_qr_codes, icon: BarChart3, color: "text-sky-600", bg: "bg-sky-50" },
  ];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="admin-dashboard">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Dashboard</h2>
        <p className="text-sm text-gray-500 mt-0.5">MyErba Loyalty Platform overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </div>
            <p className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tier Distribution */}
      <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm" data-testid="tier-distribution">
        <h3 className="font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Tier Distribution</h3>
        <div className="flex gap-4">
          {Object.entries(data.tier_distribution || {}).map(([tier, count]) => (
            <div key={tier} className="flex-1 rounded-xl p-4 text-center relative overflow-hidden" style={tierMetalStyle(tier)} data-testid={`tier-dist-${tier.toLowerCase()}`}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
              <p className="text-2xl font-black relative" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{count}</p>
              <p className="text-xs font-bold mt-1 uppercase tracking-wider relative">{tier}</p>
            </div>
          ))}
          {Object.keys(data.tier_distribution || {}).length === 0 && <p className="text-gray-400 text-sm">No users yet</p>}
        </div>
      </div>

      {/* Recent Scans */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden" data-testid="recent-scans-table">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Recent Scans</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-600 text-xs uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 font-bold text-gray-600 text-xs uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 font-bold text-gray-600 text-xs uppercase tracking-wider">Type</th>
                <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase tracking-wider">Points</th>
                <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.recent_scans || []).map((scan, i) => (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{scan.user_id?.slice(-6)}</td>
                  <td className="px-4 py-3 text-gray-600">{scan.product_name || scan.product_code}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${scan.reagent_type === "open" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-sky-50 text-sky-600 border border-sky-100"}`}>
                      {scan.reagent_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600">+{scan.points_earned}</td>
                  <td className="px-4 py-3 text-right text-gray-400">{scan.scanned_at ? new Date(scan.scanned_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))}
              {(data.recent_scans || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No scans yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
